import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { Alert, AlertIcon, Button, useToast } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { useApi } from "./api";
export const SessionContext = createContext(null);
export const useSession = () => useContext(SessionContext);
function Session({ auth: rawAuth, children }) {
  const request = useApi(),
    toast = useToast(),
    location = useLocation();
  const [viewer, setViewer] = useState(null),
    [favorites, setFavorites] = useState(new Set()),
    [favoriteBusy, setFavoriteBusy] = useState(new Set()),
    [recent, setRecent] = useState([]),
    [libraryVersion, setVersion] = useState(0),
    [sessionError, setSessionError] = useState("");
  const pending = useRef(new Set()),
    alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const api = useCallback(
    async (...args) => {
      try {
        return await request(...args);
      } catch (e) {
        if (alive.current && (e.status === 401 || e.status === 403)) {
          setFavorites(new Set());
          setRecent([]);
          setSessionError(e.message);
        }
        throw e;
      }
    },
    [request],
  );
  const refreshLibrary = useCallback(() => setVersion((v) => v + 1), []);
  const auth = {
    ...rawAuth,
    login: () =>
      rawAuth.login({
        state: { returnTo: location.pathname + location.search },
      }),
    register: () =>
      rawAuth.register({
        state: { returnTo: location.pathname + location.search },
      }),
  };
  useEffect(() => {
    if (rawAuth.isLoading) return;
    const abort = new AbortController();
    api("/api/me", { signal: abort.signal })
      .then((r) => r.json())
      .then((d) => {
        if (!abort.signal.aborted) setViewer(d);
      })
      .catch(() => {});
    return () => abort.abort();
  }, [api, rawAuth.isLoading]);
  useEffect(() => {
    if (!rawAuth.isAuthenticated || rawAuth.isLoading || sessionError) return;
    const abort = new AbortController();
    Promise.all([
      api("/api/favorites/ids", { signal: abort.signal }).then((r) => r.json()),
      api("/api/conversations", { signal: abort.signal }).then((r) => r.json()),
    ])
      .then(([saved, chats]) => {
        if (abort.signal.aborted) return;
        setFavorites(new Set(saved.ids));
        setRecent(chats.results.slice(0, 5));
      })
      .catch((e) => {
        if (e.name !== "AbortError" && e.status !== 401 && e.status !== 403)
          toast({
            title: "Could not sync your library",
            description: e.message,
            status: "error",
            isClosable: true,
          });
      });
    return () => abort.abort();
  }, [
    api,
    rawAuth.isAuthenticated,
    rawAuth.isLoading,
    libraryVersion,
    sessionError,
    toast,
  ]);
  const toggleFavorite = async (publicId) => {
    if (!rawAuth.isAuthenticated) {
      toast({
        title: "Sign in to save characters",
        status: "info",
        isClosable: true,
      });
      return;
    }
    if (pending.current.has(publicId)) return;
    pending.current.add(publicId);
    setFavoriteBusy(new Set(pending.current));
    try {
      const saved = favorites.has(publicId);
      await api(`/api/favorites/${publicId}`, {
        method: saved ? "DELETE" : "PUT",
      });
      if (!alive.current) return;
      setFavorites((prev) => {
        const next = new Set(prev);
        saved ? next.delete(publicId) : next.add(publicId);
        return next;
      });
      refreshLibrary();
    } catch (e) {
      if (alive.current)
        toast({
          title: "Could not update saved characters",
          description: e.message,
          status: "error",
          isClosable: true,
        });
    } finally {
      pending.current.delete(publicId);
      if (alive.current) setFavoriteBusy(new Set(pending.current));
    }
  };
  return (
    <SessionContext.Provider
      value={{
        api,
        auth,
        viewer,
        favorites,
        favoriteBusy,
        recent,
        toggleFavorite,
        refreshLibrary,
        libraryVersion,
      }}
    >
      {sessionError ? (
        <div className="empty-state" role="alert">
          <Alert status="warning">
            <AlertIcon />
            {sessionError}
          </Alert>
          <Button variant="brand" onClick={auth.login}>
            Sign in again
          </Button>
          <Button onClick={() => rawAuth.logout()}>Log out</Button>
        </div>
      ) : (
        children
      )}
    </SessionContext.Provider>
  );
}
export function SessionBoundary({ children }) {
  const auth = useKindeAuth();
  return (
    <Session
      key={auth.isAuthenticated ? auth.user?.id || "authenticated" : "guest"}
      auth={auth}
    >
      {children}
    </Session>
  );
}
